import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as notificationActions from "../../../actions/notificationActions";
import * as stripeActions from "../../../actions/stripeActions";
import * as userActions from "../../../actions/userActions";
import * as billingActions from "../../../actions/billingActions";
import { prettyOldPlanName } from "../../../utils/planStringConversion";
import SecondaryMenu from "../../Global/Menus/SecondaryMenu";
import AccountMenu from "../AccountMenu";
import PageContainer from "../../Global/Containers/PageContainer";
import MaxWidthContainer from "../../Global/Containers/MaxWidthContainer";
import YourPlanForm from "./YourPlanForm";
import InlineMessage from "../../Global/Messages/InlineMessage";
import ModalContainer from "../../Global/Modals/ModalContainer";
import ConfirmModal from "../../Global/Modals/ConfirmModal";
import LazyLoad from "../../Global/Loaders/LazyLoad";
import SpinLoader from "../../Global/Loaders/SpinLoader";
import LegacyPlanMessage from "./LegacyPlanMessage";
import CheckboxInput from "../../Global/Forms/CheckboxInput";

class YourPlanPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPlanType: ``,
      currentBillingFrequency: ``,
      signupOptionsLoading: true,
      applyDiscountLoading: false,
      downgradeModalOpen: false,
      submitting: false,
      values: {},
      acceptedTerms: false,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.signup !== nextProps.signup) {
      this.setState({
        currentPlanType: nextProps.signup.user_details.plan,
        currentBillingFrequency: nextProps.signup.user_details.plan_freq,
        signupOptionsLoading: false,
      });
    }

    // If the user has entered a discount code and we've had a response back from Stripe API
    if (this.props.stripe.discount !== nextProps.stripe.discount) {
      this.handleDiscountResponse(nextProps.stripe.discount);
    }
  }

  handleDiscountResponse = (discount) => {
    this.setState({ applyDiscountLoading: false });
    if (discount.exists === true) {
      this.props.actions.addNotification({
        message: `Promotion code ${discount.code} successfully applied (${discount.percent_off}% off)`,
        level: `success`,
        autoDismiss: 0,
      });
    } else {
      this.props.actions.addNotification({
        message: `The promotional code you have entered has not been recognised. Please confirm the code and try again.`,
        level: `error`,
        autoDismiss: 0,
      });
    }
  };

  handleApplyDiscountClick = (discountCode) => {
    this.setState({ applyDiscountLoading: true });
    this.props.actions.checkDiscountCodeIsValid(discountCode);
  };

  checkIfDowngrading = () => {
    const { planType, billingFrequency } = this.state.values;
    const { currency, user_details } = this.props.signup;
    this.props.actions
      .planMoveDirection(
        user_details.plan_full,
        this.buildPlanString(planType, currency, billingFrequency),
      )
      .then((res) => {
        const moveDirection = res.direction;
        if (moveDirection === `Downgrade`) {
          this.setState({ submitting: false, downgradeModalOpen: true });
        } else {
          this.updatePlan();
        }
      });
  };

  buildPlanString = (planType, currency, billingFrequency) => {
    let builtPlanString = `${planType}_${currency}`;
    if (planType !== `basic`) {
      builtPlanString = builtPlanString.concat(`_${billingFrequency}`);
    }
    return builtPlanString;
  };

  handleChangePlanSubmit = (values) => {
    this.setState({ submitting: true, values }, () => {
      this.checkIfDowngrading();
    });
  };

  updateCard = (stripeToken) => {
    this.props.actions
      .updateCard(stripeToken)
      .then(() => this.updatePlan())
      .catch(() => this.stopSubmission());
  };

  updatePlan = () => {
    const { planType, billingFrequency, discountCode } = this.state.values;
    const { currency } = this.props.signup;

    this.props.actions
      .updatePlan(
        this.buildPlanString(planType, currency, billingFrequency),
        `1`,
        discountCode,
        `/account/billing/payment-history?success=true`,
        `/account/billing/details?success=false`,
      )
      .then(() => this.stopSubmission())
      .catch(() => this.stopSubmission());
  };

  closeModal = (modal) => {
    this.setState({ [modal]: false });
  };

  stopSubmission = () => {
    this.setState({ submitting: false });
  };

  handleDowngradeConfirmClick = () => {
    if (this.state.acceptedTerms === false) {
      this.props.actions.addNotification({
        message: `You must agree to our terms in order to create an account`,
        level: `warning`,
        autoDismiss: 4,
      });
    } else {
      this.setState({ submitting: true });
      this.updatePlan();
      this.props.actions.updateAwaitingDowngrade(true);
      this.closeModal(`downgradeModalOpen`);
      this.setState({ acceptedTerms: false });
    }
  };

  onAcceptTermsChange = () => {
    this.setState({ acceptedTerms: !this.state.acceptedTerms });
  };

  render() {
    const { loggedInUser, signup, stripe, billing } = this.props;
    const {
      signupOptionsLoading,
      applyDiscountLoading,
      downgradeModalOpen,
      submitting,
      values,
      acceptedTerms,
    } = this.state;

    // Billing period end date optimising
    let nextPayDate = ``;
    if (billing.billingPeriodEnd) {
      const nextPayTimeSplit = billing.billingPeriodEnd.split(`T`);
      const nextPayDateSplit = nextPayTimeSplit[0].split(`-`);
      nextPayDate = `${nextPayDateSplit[2]}-${nextPayDateSplit[1]}-${nextPayDateSplit[0]}`;
    }

    return (
      <div id="your-plan-page">
        <PageContainer classes="bg-white">
          <SecondaryMenu>
            <AccountMenu
              active="plan"
              billingEnabled={loggedInUser.billing_enabled}
            />
          </SecondaryMenu>

          <MaxWidthContainer>
            <h1 className="like-h2 m-t-5 m-b-1">Your Plan</h1>
            <p className="m-b-5 like-h5 color-grey max-width-paragraph">
              Here you can view and change your current plan and billing
              frequency. If you require additional help please contact us at
              {` `}
              <a
                href="mailto:help@thecoachingmanual.com?Subject=Help from 'Your Plan' page"
                className="link"
              >
                help@thecoachingmanual.com
              </a>
              .
            </p>

            {`user_details` in signup && billing.awaitingDowngrade && (
              <InlineMessage classes="m-b-5">
                <p className="m-b-0 like-h5">
                  Your plan is set to downgrade at the end of your current
                  billing period ({nextPayDate}).
                </p>
              </InlineMessage>
            )}

            {`user_details` in signup && signup.user_details.access_upgraded && (
              <InlineMessage classes="m-b-5">
                <p className="m-b-0 like-h5">
                  You have been assigned a{` `}
                  <span className="fw-bold text-capitalize">
                    {signup.user_details.computed_access}
                  </span>
                  {` `}
                  licence. The table below shows your current personal plan.
                </p>
              </InlineMessage>
            )}

            <LegacyPlanMessage signup={signup} />

            {signupOptionsLoading ? (
              <LazyLoad>
                <SpinLoader black />
              </LazyLoad>
            ) : (
              <YourPlanForm
                signup={signup}
                stripe={stripe}
                signupOptionsLoading={signupOptionsLoading}
                onApplyDiscountClick={this.handleApplyDiscountClick}
                applyDiscountLoading={applyDiscountLoading}
                onSubmit={this.handleChangePlanSubmit}
                submitting={submitting}
              />
            )}

            <ModalContainer
              isOpen={downgradeModalOpen}
              closeModal={() => this.closeModal(`downgradeModalOpen`)}
              label="Downgrade plan modal"
            >
              <ConfirmModal
                title="Are you sure?"
                confirmText="Downgrade plan"
                onConfirmClick={this.handleDowngradeConfirmClick}
                onCloseClick={() => this.closeModal(`downgradeModalOpen`)}
              >
                {values.planType && (
                  <div>
                    <p>
                      Are you sure you want to downgrade your plan from{` `}
                      <span className="fw-medium text-capitalize">
                        {prettyOldPlanName(signup.user_details.plan)}
                      </span>
                      {` `}
                      to{` `}
                      <span className="fw-medium text-capitalize">
                        {values.planType}
                      </span>
                      ? You will lose access to all the great benefits of the
                      {` `}
                      <span className="fw-medium text-capitalize">
                        {prettyOldPlanName(signup.user_details.plan)}
                      </span>
                      {` `}
                      plan.
                    </p>
                    <CheckboxInput
                      name="acceptTerms"
                      label='By accepting you agree to our <a href="https://www.thecoachingmanual.com/terms/" target="_blank">terms</a>.'
                      value="acceptTerms"
                      checked={acceptedTerms}
                      onChange={this.onAcceptTermsChange}
                    />
                  </div>
                )}
              </ConfirmModal>
            </ModalContainer>
          </MaxWidthContainer>
        </PageContainer>
      </div>
    );
  }
}

YourPlanPage.propTypes = {
  loggedInUser: PropTypes.object.isRequired,
  signup: PropTypes.object.isRequired,
  stripe: PropTypes.object.isRequired,
  billing: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    loggedInUser: state.loggedInUser,
    signup: state.signup,
    stripe: state.stripe,
    billing: state.billing,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      Object.assign(
        {},
        notificationActions,
        stripeActions,
        userActions,
        billingActions,
      ),
      dispatch,
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(YourPlanPage);



// WEBPACK FOOTER //
// ./library/app/components/Account/YourPlan/YourPlanPage.js